import { useContext, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Slider } from '@elfsight/lib-toolkit-slider';
import { getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import {
  classnamePrefix,
  MOBILE_WIDTH,
  TABLET_WIDTH_LARGE
} from '../../../constants';
import { processSource } from '../../helpers';
import { sourceContext } from '../../SourceContext';
import { Header } from '../common/header';
import { currentVideoContext } from '../currentVideoContext';
import { videosContext } from '../videos/VideosContext';
import { Player, VideoCard } from '../videos/video';
import { Info } from '../videos/video/info';

const COLUMN_BASIS = 160;

export const SliderLayout = ({
  hasNext,
  loadNext,
  nextVideo,
  data,
  widgetWidth
}) => {
  const sliderRef = useRef(null);
  const prevDataLengthRef = useRef(data.length);
  const skipNextOnTranslateEndRef = useRef(false);
  const { currentVideo } = useContext(currentVideoContext);
  const { videosSource } = useContext(sourceContext);
  const {
    playMode,
    headerVisible,
    sliderItemsPerView: sliderColumnsDesktop,
    sliderColumnsMobile,
    sliderColumnsTablet,
    sliderArrows,
    sliderSpeed,
    sliderAutoplay,
    sliderPaginationType,
    sliderDrag,
    textColor,
    playerElements,
    displayDeactivation
  } = useSettings();

  const [columnsCount, setColumnsCount] = useState(5);
  const [sliderIsDrag, setSliderDrag] = useState(false);
  const [{ type }] = useState(processSource({ source: videosSource }));
  const derivedPaginationType =
    sliderPaginationType === 'dots' ? 'bullets' : sliderPaginationType;

  useLayoutEffect(() => {
    const clientWidthSlider = sliderRef.current?.slider?.current.clientWidth;

    let sliderItemsPerView;

    if (widgetWidth < MOBILE_WIDTH) {
      sliderItemsPerView = sliderColumnsMobile;
    } else if (widgetWidth < TABLET_WIDTH_LARGE) {
      sliderItemsPerView = sliderColumnsTablet;
    } else {
      sliderItemsPerView = sliderColumnsDesktop;
    }

    const fixedCount =
      sliderItemsPerView !== 'auto' && !Number.isNaN(sliderItemsPerView);

    const nextColumnsCount = fixedCount
      ? parseInt(sliderItemsPerView, 10)
      : Math.floor(clientWidthSlider / COLUMN_BASIS);

    if (nextColumnsCount !== columnsCount) {
      setColumnsCount(nextColumnsCount);
    }
  });

  useLayoutEffect(() => {
    const shouldLoadMore =
      sliderRef.current.state.currentIndex + columnsCount >= data.length &&
      hasNext;

    if (shouldLoadMore) {
      loadNext();
    }
  }, [columnsCount, data.length, hasNext, loadNext]);

  const onTranslateEnd = () => {
    setSliderDrag(false);

    if (skipNextOnTranslateEndRef.current) {
      skipNextOnTranslateEndRef.current = false;
      return;
    }

    const shouldLoadMore =
      sliderRef &&
      sliderRef.current.state.realIndex + columnsCount >= data.length &&
      hasNext;

    if (shouldLoadMore) {
      loadNext();
    }
  };

  useLayoutEffect(() => {
    if (data.length > prevDataLengthRef.current && sliderRef.current) {
      const prevIndex = sliderRef.current.state.currentIndex;
      skipNextOnTranslateEndRef.current = true;
      sliderRef.current.goTo(prevIndex, true);
      prevDataLengthRef.current = data.length;
    }
  }, [data.length]);

  const arrowNavigationBackgroundColor = textColor
    ? getColorWithAlpha(textColor, 0.06)
    : 'rgba(26, 46, 59, 0.06)';

  const arrowNavigationBackgroundColorHover = textColor
    ? getColorWithAlpha(textColor, 0.2)
    : 'rgba(26, 46, 59, 0.2)';

  return (
    <Component>
      {headerVisible && type !== 'videos' && <Header />}

      {playMode === 'player' && currentVideo && (
        <Video>
          <PlayerWrapper>
            <Player item={currentVideo} nextVideo={nextVideo} />
          </PlayerWrapper>

          <Info data={currentVideo} elements={playerElements} />
        </Video>
      )}
      <Videos
        _sliderPaginationType={derivedPaginationType}
        _sliderArrows={sliderArrows}
        _isWidgetDeactivated={displayDeactivation}
        _dataLength={data.length}
        _columnsCount={columnsCount}
        _textColor={textColor}
      >
        <videosContext.Provider
          value={{
            isDrag: sliderIsDrag
          }}
        >
          <Slider
            appClassnamePrefix={classnamePrefix}
            paginationClassName={`${classnamePrefix}-pagination`}
            itemClassName={`${classnamePrefix}-slider-item`}
            navigationClassName={`${classnamePrefix}-slider-navigation`}
            slidesPerView={columnsCount}
            ref={sliderRef}
            onTranslateStart={() => setSliderDrag(true)}
            onTranslateEnd={onTranslateEnd}
            arrows={sliderArrows}
            shouldUseCombinedNavigation={false}
            drag={sliderDrag}
            speed={sliderSpeed}
            autoplayDelay={displayDeactivation ? 0 : Number(sliderAutoplay)}
            paginationType={derivedPaginationType}
            paginationAlign="center"
            paginationPosition="outside"
            arrowsPosition="outside"
            colors={{
              pagination: {
                color: textColor,
                active: textColor
              },
              arrows: {
                color: textColor || 'rgb(26, 46, 59)',
                background: arrowNavigationBackgroundColor,
                hover: {
                  color: textColor || 'rgb(26, 46, 59)',
                  background: arrowNavigationBackgroundColorHover
                }
              }
            }}
          >
            {data.map((item, id) => (
              <VideoCard
                data={item}
                key={id}
                number={id + 1}
                count={data.length}
              />
            ))}
          </Slider>
        </videosContext.Provider>
      </Videos>
    </Component>
  );
};

const PlayerWrapper = styled.div.attrs({
  className: `${classnamePrefix}-slider-player`
})`
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Videos = styled.div.attrs({
  className: `${classnamePrefix}-slider-videos`
})`
  margin-top: 24px;
  position: relative;

  ${({ _sliderPaginationType }) =>
    _sliderPaginationType !== 'none' &&
    css`
      padding-bottom: 40px;
    `}

  & .${classnamePrefix}-slider-item {
    padding: 0 10px;
    height: auto;
  }

  & .${classnamePrefix}-slider-outer {
    margin: 0 38px;
    transition: 0.3s ease margin;
    ${({ _sliderArrows, _dataLength, _columnsCount }) =>
      (!_sliderArrows || _dataLength <= _columnsCount) &&
      css`
        margin: 0;
      `}
  }

  & .${classnamePrefix}-navigation-arrow {
    display: flex;
    border-radius: 4px;
    width: 28px;
    box-shadow: none;
    max-height: 120px;
    height: 100%;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.85;

    ${({ _isWidgetDeactivated }) =>
      _isWidgetDeactivated &&
      css`
        pointer-events: none;
      `}

    &:hover {
      opacity: 1;
    }
  }

  &
    .${classnamePrefix}-navigation-previous
    .${classnamePrefix}-slider-navigation-arrow-icon {
    right: 8px;
  }

  &
    .${classnamePrefix}-navigation-next
    .${classnamePrefix}-slider-navigation-arrow-icon {
    left: 8px;
  }

  & .${classnamePrefix}-pagination, & .${classnamePrefix}-pagination-component {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 150%);
  }

  & .${classnamePrefix}-pagination-component {
    transform: translate(-50%, 100%);
  }

  & .${classnamePrefix}-bullet-item .${classnamePrefix}-pagination-dot {
    transform: scale(0.33);
    opacity: 0;
  }

  & .${classnamePrefix}-bullet-item {
    cursor: default;
    pointer-events: none;
  }

  &
    .${classnamePrefix}-bullet-item.${classnamePrefix}-pagination-item-active
    .${classnamePrefix}-pagination-dot {
    transform: scale(1);
    opacity: 1;
  }

  &
    .${classnamePrefix}-bullet-prev-prev
    .${classnamePrefix}-pagination-dot,
    &
    .${classnamePrefix}-bullet-next-next
    .${classnamePrefix}-pagination-dot {
    opacity: 0.3;
  }

  &
    .${classnamePrefix}-bullet-prev
    .${classnamePrefix}-pagination-dot,
    &
    .${classnamePrefix}-bullet-next
    .${classnamePrefix}-pagination-dot {
    transform: scale(0.66);
    opacity: 0.6;
  }

  &
    .${classnamePrefix}-bullet-prev,
    &
    .${classnamePrefix}-bullet-prev-prev,
    &
    .${classnamePrefix}-bullet-next,
    &
    .${classnamePrefix}-bullet-next-next {
    cursor: pointer;
    pointer-events: ${({ _isWidgetDeactivated }) =>
      _isWidgetDeactivated ? 'none' : 'auto'};
  }

  &
    .${classnamePrefix}-bullet-prev:hover
    .${classnamePrefix}-bullet-dot,
    &
    .${classnamePrefix}-bullet-prev-prev:hover
    .${classnamePrefix}-bullet-dot,
    &
    .${classnamePrefix}-bullet-next:hover
    .${classnamePrefix}-bullet-dot,
    &
    .${classnamePrefix}-bullet-next-next:hover
    .${classnamePrefix}-bullet-dot {
    transform: scale(1);
    opacity: 1;
  }
`;

const Component = styled.div.attrs({
  className: `${classnamePrefix}-slider-component`
})`
  width: 100%;

  & .${classnamePrefix}-slider-slider {
    overflow: visible;
  }

  & .${classnamePrefix}-video-card-container {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    height: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0;

    &:after {
      display: none;
    }
  }
`;

const Video = styled.div.attrs({
  className: `${classnamePrefix}-slider-video`
})``;
